import { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./TallyInLinePage.scss";
import { useLocation, useNavigate } from "react-router";
import { Button, Modal } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { toast } from "react-toastify";
import { observer } from "mobx-react";
import * as itemService from "../../Services/Http/ItemService";
import * as orderService from "../../Services/Http/OrderService";
import { useOrder } from "../../Context/useOrder";
import { useKeyDown } from "../../Hooks/useKeyDown";
import { PostTalliesRequest } from "../../Models/PostTalliesRequest";
import { TransHeaderResponse } from "../../Models/TransHeaderResponse";
import dataStore from "../../Store/dataStore";
import tallyStore from "../../Store/tallyStore";
import { Order } from "../../Models/Order";
import { Product } from "../../Models/Product";
import { TallyBarcodeResponse, SetStockBarcodeResponse } from "../../Models/TallyBarcodeResponse";
import { TallyDraft, sumTallyEffectQty } from "../../Models/TallyDraft";
import { TallyRecord, tallyEffectStock } from "../../Models/TallyRecord";
import { PostedTally } from "../../Models/PostedTally";
import { parseGs1Barcode, isTallyableGs1, Gs1Barcode } from "../../Helpers/Gs1Parser";
import { parseGs1WithRules, hasTallyRules } from "../../Helpers/Gs1Rules";
import { getSettingIgnoreCase } from "../../Helpers/SettingsHelper";
import { useAuth } from "../../Context/useAuth";
import { tallyRoutes, tallyTransTypeFor } from "../../Helpers/TallyMode";
import OrderStatusBar from "../../Components/OrderStatusBar/OrderStatusBar";

interface Props {}


const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const questionSound = new Audio();
questionSound.src = "/assets/sounds/question.mp3";
questionSound.load();

const TallyInLinePage = observer((props: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = tallyRoutes(location.pathname);
  const transType = tallyTransTypeFor(location.pathname);
  const { getCurrentOrder, setCurrentOrder } = useOrder();
  const { getSetting } = useAuth();
  const order: Order | null = getCurrentOrder();
  const detailNo: number | undefined = (location.state as any)?.detailNo;
  const line: Product | undefined = order?.cartLines?.find((l) => l.detailNo === detailNo);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [clearModalOpen, setClearModalOpen] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [deletePosted, setDeletePosted] = useState<PostedTally | null>(null);
  //the scan waiting on "this stockcode has no stock barcode - use this one?"
  const [missingBarcodePrompt, setMissingBarcodePrompt] = useState<{ barcode: string; parsed: Gs1Barcode } | null>(null);
  const [isSavingBarcode, setIsSavingBarcode] = useState<boolean>(false);
  const scanBoxInputRef = useRef<HTMLInputElement>(null);

  const serverUrl = dataStore.serverUrl || "";
  const dbKey = dataStore.dbKey || "";
  const draft: TallyDraft | undefined =
    order?.transRefCode && detailNo !== undefined ? tallyStore.getDraft(serverUrl, dbKey, order.transRefCode, detailNo) : undefined;
  const tallies: TallyRecord[] = draft?.tallies || [];

  useLayoutEffect(() => {
    if (scanBoxInputRef.current) {
      scanBoxInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    //no line to tally (e.g. the page was opened directly), go back
    if (!order || !order.transRefCode) {
      navigate(routes.list);
    } else if (!line) {
      navigate(routes.lines);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, line]);

  useKeyDown((event: any) => {
    onKeyPressed(event);
  }, []);

  const onScanFailed = (message: string) => {
    errorSound.play();
    toast.error(message, { autoClose: 1500 });
    setSearchTerm("");
  };

  const addTally = (parsed: Gs1Barcode) => {
    if (!line || !order?.transRefCode || detailNo === undefined) {
      return;
    }
    //the barcode's use-by wins; the line's LineUserDate2 is the fallback. When the label
    //carries one and it disagrees with the line, flag it rather than silently preferring one.
    const lineUseBy = (line.lineUseByDate || "").substring(0, 10);
    const useByMismatch = !!parsed.useByDate && !!lineUseBy && parsed.useByDate !== lineUseBy;

    const tally: TallyRecord = {
      id: `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
      tallySerialNo: parsed.serialNo!,
      tallyQty: parsed.netWeight!,
      tallyEffect: tallyEffectStock,
      tallyEffectQty: parsed.netWeight!,
      useByDate: parsed.useByDate || line.lineUseByDate,
      useByMismatch,
      //a meat carton carries a pack date (13) rather than a production date (11)
      mfgDate: parsed.mfgDate || parsed.packDate,
      gtin: parsed.gtin,
      batch: parsed.batch,
      scannedAt: new Date().toISOString(),
    };
    tallyStore.addTally(serverUrl, dbKey, order.transRefCode, detailNo, tally, line.stockCode, line.stockNo);
    setSearchTerm("");
    successSound.play();
    toast.success(`Tallied ${parsed.netWeight!.toLocaleString(navigator.language, { maximumFractionDigits: 3 })} kg`, { autoClose: 300 });
    if (useByMismatch) {
      questionSound.play();
      toast.warning(`Warning Tally UseByDate ${parsed.useByDate} differs to line ${lineUseBy}`, { autoClose: false });
    }
  };

  const onItemScanned = (term?: string) => {
    const barcode = (term || searchTerm || "").trim();
    if (!barcode || !line || !order?.transRefCode || detailNo === undefined || missingBarcodePrompt) {
      return;
    }
    //PDATallyGS1BarcodeRules overrides how the barcode is read; without it, standard GS1 AIs
    const gs1Rules = getSetting(serverUrl, dbKey, "PDATallyGS1BarcodeRules") || getSettingIgnoreCase("PDATallyGS1BarcodeRules");
    const parsed = hasTallyRules(gs1Rules) ? parseGs1WithRules(barcode, gs1Rules!) : parseGs1Barcode(barcode);
    if (!isTallyableGs1(parsed)) {
      onScanFailed("Barcode is not a GS1 tally label (needs serial and weight)");
      return;
    }
    //a carton is tallied once - reject a repeat of the same label, pending or already posted
    if (tallies.some((t) => t.tallySerialNo === parsed.serialNo)) {
      onScanFailed(`Serial ${parsed.serialNo} already tallied`);
      return;
    }
    if ((line.postedTallies || []).some((p) => p.tallySerialNo === parsed.serialNo)) {
      onScanFailed(`Serial ${parsed.serialNo} already posted`);
      return;
    }
    //pda_TallyBarcodeLookup checks the barcode belongs to this line's stock before we count it
    const stockNo = Number(line.stockNo);
    if (!stockNo) {
      onScanFailed("Wrong barcode for stock");
      return;
    }
    itemService
      .validateTallyBarcodeAPI(stockNo, barcode, serverUrl, dbKey)
      .then((resp: TallyBarcodeResponse) => {
        if (!resp || !resp.isTrue) {
          onScanFailed("Wrong barcode for stock");
          return;
        }
        //the scan passed only because the stock has no barcode on file - offer to save this one
        //against it, unless the operator has already said Ignore for this line
        if (!resp.hasStockBarcode && !draft?.ignoreMissingStockBarcode) {
          questionSound.play();
          setMissingBarcodePrompt({ barcode, parsed });
          return;
        }
        addTally(parsed);
      })
      .catch(() => {
        onScanFailed("Wrong barcode for stock");
      });
  };

  //Yes - save the scanned barcode onto the stock, then count the carton. The tally goes on
  //either way: a barcode that could not be saved is a stock maintenance problem, not a bad scan.
  const onUseScannedBarcode = () => {
    const pending = missingBarcodePrompt;
    const stockNo = Number(line?.stockNo);
    if (!pending || !stockNo) {
      setMissingBarcodePrompt(null);
      return;
    }
    setIsSavingBarcode(true);
    itemService
      .setTallyStockBarcodeAPI(stockNo, pending.barcode, serverUrl, dbKey)
      .then((resp: SetStockBarcodeResponse) => {
        setIsSavingBarcode(false);
        setMissingBarcodePrompt(null);
        if (resp && resp.isTrue) {
          toast.success(`Barcode ${resp.stockBarcode || pending.parsed.gtin || ""} saved to ${line?.stockCode}`, { autoClose: 900 });
        } else {
          errorSound.play();
          toast.error(resp?.errorMessage || "Could not save the barcode - tally kept", { autoClose: false });
        }
        addTally(pending.parsed);
      })
      .catch((error: any) => {
        setIsSavingBarcode(false);
        setMissingBarcodePrompt(null);
        errorSound.play();
        toast.error(`${error?.message || "Could not save the barcode"} - tally kept`, { autoClose: false });
        addTally(pending.parsed);
      });
  };

  //No, and Ignore - the stock keeps no barcode and the carton is counted anyway. Ignore also
  //stops the prompt coming back for the rest of this line's tally.
  const onDeclineScannedBarcode = (ignoreForLine: boolean) => {
    const pending = missingBarcodePrompt;
    setMissingBarcodePrompt(null);
    if (!pending) {
      return;
    }
    if (ignoreForLine && order?.transRefCode && detailNo !== undefined) {
      tallyStore.setIgnoreMissingStockBarcode(serverUrl, dbKey, order.transRefCode, detailNo, line?.stockCode, line?.stockNo);
    }
    addTally(pending.parsed);
  };

  const onKeyPressed = (event: any) => {
    //a scan arriving while the barcode prompt is up would otherwise pile into the search box
    if (missingBarcodePrompt) {
      return;
    }
    if (event.target && event.target instanceof HTMLInputElement && event.target.name === scanBoxInputRef.current?.name) {
      return;
    }
    const keyPressed = event.key ? event.key : "";
    if (keyPressed === "") {
      return;
    }
    if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
      event.preventDefault();
      setSearchTerm((oldState) => {
        onItemScanned(oldState);
        return oldState;
      });
    } else if (keyPressed.length === 1) {
      setSearchTerm((oldState) => oldState + keyPressed);
    }
  };

  if (!order || !line) {
    return null;
  }

  const handleScanChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const handleScanKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onItemScanned();
    }
  };

  const onRemoveTally = (tally: TallyRecord) => {
    if (!order.transRefCode || detailNo === undefined) {
      return;
    }
    tallyStore.removeTally(serverUrl, dbKey, order.transRefCode, detailNo, tally.id);
  };

  //post this line's tallies as T_Tally records; the draft only goes once esuite has taken them
  const onPost = () => {
    if (!order.transRefCode || detailNo === undefined || tallies.length === 0) {
      toast.error("Nothing to post");
      return;
    }
    setIsPosting(true);
    const model: PostTalliesRequest = {
      transNo: order.transNo,
      transRefCode: order.transRefCode,
      transType,
      lineSerialNo: line.serialNo,
      parentDetailNo: line.parentDetailNo,
      orderCompanyNo: order.companyNo,
      detailNo,
      stockNo: line.stockNo,
      transDate: new Date().toISOString().substring(0, 10),
      accountCompanyNo: order.accountCompanyNo,
      accountNo: order.accountNo,
      accountRef: order.accountRef,
      tallies: tallies.map((t) => ({
        tallySerialNo: t.tallySerialNo,
        tallyQty: t.tallyQty,
        useByDate: t.useByDate,
        mfgDate: t.mfgDate,
        serialNo: t.serialNo,
        binNo: t.binNo,
        locationNo: t.locationNo,
      })),
    };
    itemService
      .postTalliesAPI(model, serverUrl, dbKey)
      .then((resp: TransHeaderResponse) => {
        setIsPosting(false);
        if (resp && resp.errorMessage) {
          errorSound.play();
          toast.error(resp.errorMessage, { autoClose: false });
          return;
        }
        tallyStore.removeDraft(serverUrl, dbKey, order.transRefCode!, detailNo);
        successSound.play();
        toast.success("Tallies posted", { autoClose: 600 });
        //re-read the transaction so the line list shows the totals we just wrote
        orderService
          .getTallyInTransactionDetailsAPI(order.transRefCode!, serverUrl, dbKey, transType)
          .then((updated: Order) => {
            if (updated) {
              updated.transDueDate = updated.transDueDate ? new Date(updated.transDueDate) : new Date();
              updated.transDate = updated.transDate ? new Date(updated.transDate) : new Date();
              updated.dueDate = updated.dueDate ? new Date(updated.dueDate) : new Date();
              setCurrentOrder(updated);
            }
            navigate(routes.lines);
          })
          .catch(() => {
            navigate(routes.lines);
          });
      })
      .catch((error: any) => {
        setIsPosting(false);
        errorSound.play();
        //the tallies stay on the device so nothing counted is lost
        toast.error(`${error?.message || "Post failed"} - tallies kept on device`, { autoClose: false });
      });
  };


  //clear all is the only thing that throws the pending tallies away; it stays on the screen
  //deleting a posted tally removes it from esuite and recalculates the line/serial totals
  const onDeletePostedConfirmed = () => {
    if (!deletePosted || !order.transRefCode || detailNo === undefined) {
      setDeletePosted(null);
      return;
    }
    const target = deletePosted;
    setIsClearing(true);
    itemService
      .deleteTalliesAPI(
        {
          transRefCode: order.transRefCode,
          detailNo: line?.parentDetailNo || detailNo,
          tallySerialNo: target.tallySerialNo,
        },
        serverUrl,
        dbKey,
      )
      .then((resp: TransHeaderResponse) => {
        setIsClearing(false);
        setDeletePosted(null);
        if (resp && resp.errorMessage) {
          errorSound.play();
          toast.error(resp.errorMessage, { autoClose: false });
          return;
        }
        toast.success(`Tally ${target.tallySerialNo} deleted`, { autoClose: 600 });
        refreshTransaction();
      })
      .catch((error: any) => {
        setIsClearing(false);
        setDeletePosted(null);
        errorSound.play();
        toast.error(error?.message || "Could not delete tally", { autoClose: false });
      });
  };

  //re-read so the totals the proc recalculated show straight away
  const refreshTransaction = () => {
    orderService
      .getTallyInTransactionDetailsAPI(order.transRefCode!, serverUrl, dbKey, transType)
      .then((updated: Order) => {
        if (updated) {
          updated.transDueDate = updated.transDueDate ? new Date(updated.transDueDate) : new Date();
          updated.transDate = updated.transDate ? new Date(updated.transDate) : new Date();
          updated.dueDate = updated.dueDate ? new Date(updated.dueDate) : new Date();
          setCurrentOrder(updated);
        }
      })
      .catch(() => {
        //display only - the delete already succeeded
      });
  };

  const onClearAllConfirmed = () => {
    if (!order.transRefCode || detailNo === undefined) {
      setClearModalOpen(false);
      return;
    }
    setIsClearing(true);
    //pda_DeleteTally removes what has been posted and recalculates the line/serial totals
    itemService
      .deleteTalliesAPI(
        { transRefCode: order.transRefCode, detailNo: line?.parentDetailNo || detailNo, tallySerialNo: line?.serialNo },
        serverUrl,
        dbKey,
      )
      .then((resp: TransHeaderResponse) => {
        setIsClearing(false);
        if (resp && resp.errorMessage) {
          errorSound.play();
          toast.error(resp.errorMessage, { autoClose: false });
          return;
        }
        finishClearAll();
      })
      .catch((error: any) => {
        setIsClearing(false);
        errorSound.play();
        //the pending scans are kept, so nothing counted is lost when the delete fails
        toast.error(`${error?.message || "Could not clear tallies"} - scans kept on device`, { autoClose: false });
      });
  };

  const finishClearAll = () => {
    tallyStore.removeDraft(serverUrl, dbKey, order.transRefCode!, detailNo!);
    setClearModalOpen(false);
    toast.success("Tallies cleared", { autoClose: 600 });
    //re-read so the line totals the proc recalculated show straight away
    orderService
      .getTallyInTransactionDetailsAPI(order.transRefCode!, serverUrl, dbKey, transType)
      .then((updated: Order) => {
        if (updated) {
          updated.transDueDate = updated.transDueDate ? new Date(updated.transDueDate) : new Date();
          updated.transDate = updated.transDate ? new Date(updated.transDate) : new Date();
          updated.dueDate = updated.dueDate ? new Date(updated.dueDate) : new Date();
          setCurrentOrder(updated);
        }
      })
      .catch(() => {
        //display only - the delete already succeeded
      });
    if (scanBoxInputRef.current) {
      scanBoxInputRef.current.focus();
    }
  };

  const totalQty = sumTallyEffectQty(draft);

  return (
    <>
      <OrderStatusBar />
      <div className="container-limited-width tally-in-line-page">
        <div className="row">
          <div className="col-12 my-2">
            <div className="stock-desc">{line.stockDesc}</div>
            <div className="stock-code">{line.stockCode}</div>
          </div>
        </div>

        <div className="row mb-1">
          <div className="col-12 d-flex justify-content-end">
            <Button variant="outline-danger" size="sm" onClick={() => setClearModalOpen(true)} disabled={isPosting || tallies.length === 0}>
              Clear All
            </Button>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <input
              name="scanBox"
              type="text"
              className="form-control search-box"
              placeholder="Scan GS1 barcode"
              autoCapitalize="none"
              autoComplete="off"
              value={searchTerm}
              onChange={handleScanChange}
              onKeyUp={handleScanKeyUp}
              ref={scanBoxInputRef}
            />
          </div>
        </div>

        <div className="row my-2">
          <div className="col-12 d-flex justify-content-between align-items-center totals-row">
            <span>
              Tallied <b>{(line.talliedCount || 0) + tallies.length}</b> of {line.tallyTargetQty || 0}
            </span>
            <span className="badge bg-secondary badge-pill">
              Total KGs: {((line.talliedQty || 0) + totalQty).toLocaleString(navigator.language, { maximumFractionDigits: 3 })}
            </span>
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-2">
            <ul className="list-group tally-list">
              {[...tallies].reverse().map((tally, index) => (
                <li key={tally.id} className="list-group-item d-flex justify-content-between align-items-center">
                  <div className="d-flex flex-column">
                    <b>{tally.tallySerialNo}</b>
                    <span className="tally-meta">
                      {tally.useByDate && <>Use by {tally.useByDate}</>}
                      {tally.mfgDate && <> &middot; Mfg {tally.mfgDate}</>}
                    </span>
                    {tally.useByMismatch && <span className="tally-warning">Use-by differs to line</span>}
                  </div>
                  <div className="d-flex align-items-center">
                    <span className="badge bg-secondary badge-pill me-2">
                      {tally.tallyQty.toLocaleString(navigator.language, { maximumFractionDigits: 3 })} kg
                    </span>
                    <BsTrash onClick={() => onRemoveTally(tally)} />
                  </div>
                </li>
              ))}
            </ul>
            {/* already in esuite - shown for reference, not removable from here */}
            {(line.postedTallies || []).length > 0 && (
              <ul className="list-group tally-list mt-2">
                {(line.postedTallies || []).map((posted, index) => (
                  <li
                    key={`posted_${posted.tallySerialNo}_${index}`}
                    className="list-group-item d-flex justify-content-between align-items-center posted-tally"
                  >
                    <div className="d-flex flex-column">
                      <b>{posted.tallySerialNo}</b>
                      <span className="tally-meta">
                        Posted
                        {posted.useByDate && <> &middot; Use by {posted.useByDate}</>}
                        {posted.mfgDate && <> &middot; Mfg {posted.mfgDate}</>}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-secondary badge-pill me-2">
                        {(posted.tallyQty || 0).toLocaleString(navigator.language, { maximumFractionDigits: 3 })} kg
                      </span>
                      <BsTrash onClick={() => setDeletePosted(posted)} />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {tallies.length === 0 && (line.postedTallies || []).length === 0 && (
              <div className="my-3 text-center text-muted">Nothing tallied yet.</div>
            )}
          </div>
        </div>

      </div>

      <div className="tally-post-footer">
        <div className="container-limited-width">
          <Button variant="primary" className="w-100" onClick={onPost} disabled={isPosting || tallies.length === 0}>
            {isPosting ? "Posting..." : `Post Tallies${tallies.length > 0 ? ` (${tallies.length})` : ""}`}
          </Button>
        </div>
      </div>

      {/* the stock has no barcode on file - offer to teach it the one just scanned */}
      <Modal
        show={!!missingBarcodePrompt}
        onHide={() => onDeclineScannedBarcode(false)}
        centered
        backdrop="static"
        dialogClassName="missing-barcode-modal"
      >
        <Modal.Header>
          <Modal.Title>GTIN Barcode not found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <b>{line.stockCode}</b> doesn't have that GTIN barcode.
          </div>
          <div>Would you like to save this barcode?</div>
          <div className="scanned-barcode my-2">{missingBarcodePrompt?.parsed.gtin || missingBarcodePrompt?.barcode}</div>
          <span className="text-muted">The carton is tallied either way.</span>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onUseScannedBarcode} disabled={isSavingBarcode}>
            {isSavingBarcode ? "Saving..." : "Yes"}
          </Button>
          <Button variant="outline-secondary" onClick={() => onDeclineScannedBarcode(false)} disabled={isSavingBarcode}>
            No
          </Button>
          <Button variant="outline-secondary" onClick={() => onDeclineScannedBarcode(true)} disabled={isSavingBarcode}>
            Ignore all
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deletePosted} onHide={() => setDeletePosted(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete posted tally?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Tally <b>{deletePosted?.tallySerialNo}</b>
          {deletePosted?.tallyQty !== undefined && <> ({deletePosted.tallyQty} kg)</>} will be deleted from eSuite and the
          line totals recalculated. This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeletePosted(null)} disabled={isClearing}>
            Keep
          </Button>
          <Button variant="danger" onClick={onDeletePostedConfirmed} disabled={isClearing}>
            {isClearing ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={clearModalOpen} onHide={() => setClearModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clear all scans?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tallies.length > 0 && (
            <>
              {tallies.length} pending scan{tallies.length === 1 ? "" : "s"} will be discarded.<br />
            </>
          )}
          Tallies already posted for this line will be deleted from eSuite and its totals recalculated. This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setClearModalOpen(false)} disabled={isClearing}>
            Keep
          </Button>
          <Button variant="danger" onClick={onClearAllConfirmed} disabled={isClearing}>
            {isClearing ? "Clearing..." : "Clear All"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default TallyInLinePage;
